/**
 * **Notifikasi** — kotak masuk peristiwa + daftar tujuan (0068).
 *
 * ### ⛔ Kenapa kotak masuknya tetap ada walau Telegram tak disetel
 * Peristiwa diterbitkan terlepas dari ada-tidaknya penerima. Layar ini karena itu tetap menjawab
 * *"malam itu ada selisih Rp450.000 dan tak seorang pun dikabari"* — pertanyaan yang, tanpa
 * peristiwa tersimpan, tak punya jawaban sama sekali.
 *
 * ### ⚠️ Kolom "terkirim/antre/gagal" ada di tiap baris, bukan diringkas di atas
 * Notifikasi yang gagal terkirim adalah kegagalan yang paling mudah tak terlihat: layarnya terlihat
 * normal, isinya lengkap, cuma ponsel penerimanya sunyi. Nasib tiap peristiwa karena itu dicetak
 * di sebelah peristiwanya.
 */
import {
  Alert, Button, Card, Checkbox, Form, Input, Modal, Select, Space, Table, Tabs, Tag, Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { NotificationRow, NotificationTarget, Outlet } from '../api/client';

const KINDS = ['shift_variance', 'day_closed', 'sale_rejected'] as const;

export function Notifications() {
  const { t } = useTranslation();
  const [outlets, setOutlets] = useState<readonly Outlet[]>([]);
  const [outletCode, setOutletCode] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<readonly NotificationRow[]>([]);
  const [targets, setTargets] = useState<readonly NotificationTarget[]>([]);
  const [channels, setChannels] = useState<readonly string[]>([]);
  const [sunting, setSunting] = useState<NotificationTarget | 'baru' | undefined>(undefined);
  const [sibuk, setSibuk] = useState(false);
  const [form] = Form.useForm();

  const muat = useCallback(async () => {
    setSibuk(true);
    try {
      const [inbox, tg] = await Promise.all([
        api.notifications(outletCode),
        api.notificationTargets(outletCode),
      ]);
      setRows(inbox.rows);
      setTargets(tg.rows);
      setChannels(tg.availableChannels);
      /* ⭐ Membuka layarnya = membacanya. Tombol "tandai dibaca" terpisah cuma menambah langkah
         yang akan dilewati, lalu lencananya kekal menyala dan berhenti berarti apa-apa. */
      await api.notificationsSeen().catch(() => undefined);
    } catch (e) {
      void message.error((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }, [outletCode]);

  useEffect(() => {
    void api.outlets().then(setOutlets).catch(() => undefined);
  }, []);
  useEffect(() => { void muat(); }, [muat]);

  const kolomInbox: ColumnsType<NotificationRow> = [
    {
      title: t('notify.colWhen'), dataIndex: 'createdAt', width: 150,
      render: (v: string) => new Date(v).toLocaleString('id-ID'),
    },
    { title: t('notify.colOutlet'), dataIndex: 'outletCode', width: 110 },
    {
      title: t('notify.colKind'), dataIndex: 'kind', width: 150,
      render: (v: string) => <Tag>{t(`notify.kind.${v}`)}</Tag>,
    },
    {
      title: t('notify.colMessage'),
      render: (_: unknown, r) => (
        <div>
          <Typography.Text strong>{r.title}</Typography.Text>
          <div style={{ whiteSpace: 'pre-wrap' }}>{r.body}</div>
        </div>
      ),
    },
    {
      title: t('notify.colDelivery'), width: 170,
      render: (_: unknown, r) =>
        r.sent + r.pending + r.failed === 0
          ? (
            /* ⛔ "Tak ada penerima" BUKAN sukses dan bukan galat — ia keadaan tersendiri, dan ia
               yang paling sering benar-benar terjadi. */
            <Tag data-testid="notify-no-target">{t('notify.noTarget')}</Tag>
          )
          : (
            <Space size={4}>
              {r.sent > 0 && <Tag color="green">{t('notify.sent', { n: r.sent })}</Tag>}
              {r.pending > 0 && <Tag color="blue">{t('notify.pending', { n: r.pending })}</Tag>}
              {r.failed > 0 && <Tag color="red">{t('notify.failed', { n: r.failed })}</Tag>}
            </Space>
          ),
    },
  ];

  const kolomTarget: ColumnsType<NotificationTarget> = [
    { title: t('notify.colOutlet'), dataIndex: 'outletCode', width: 110 },
    { title: t('notify.colChannel'), dataIndex: 'channel', width: 100 },
    { title: t('notify.colAddress'), dataIndex: 'address' },
    { title: t('notify.colLabel'), dataIndex: 'label', render: (v: string | null) => v ?? '—' },
    {
      title: t('notify.colKinds'),
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.kinds.map((k) => <Tag key={k}>{t(`notify.kind.${k}`)}</Tag>)}
        </Space>
      ),
    },
    {
      title: t('notify.colHealth'), width: 220,
      render: (_: unknown, r) =>
        r.failed === 0
          ? <Tag color="green">{t('notify.healthy')}</Tag>
          : (
            <Space direction="vertical" size={0}>
              <Tag color="red">{t('notify.failed', { n: r.failed })}</Tag>
              {r.lastError !== null && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {r.lastError}
                </Typography.Text>
              )}
            </Space>
          ),
    },
    {
      title: '', width: 210,
      render: (_: unknown, r) => (
        <Space size={4}>
          <Button size="small" data-testid={`notify-test-${r.id}`} onClick={() => void uji(r.id)}>
            {t('notify.test')}
          </Button>
          <Button size="small" onClick={() => { bukaSunting(r); }}>{t('common.edit')}</Button>
          <Button size="small" danger onClick={() => void hapus(r.id)}>{t('common.delete')}</Button>
        </Space>
      ),
    },
  ];

  function bukaSunting(r: NotificationTarget | 'baru') {
    setSunting(r);
    form.setFieldsValue(
      r === 'baru'
        ? { outletCode: outletCode ?? outlets[0]?.code, channel: 'telegram', kinds: [...KINDS] }
        : {
            outletCode: r.outletCode, channel: r.channel, address: r.address,
            label: r.label ?? '', kinds: [...r.kinds], isActive: r.isActive,
          },
    );
  }

  async function simpan() {
    const v = await form.validateFields();
    try {
      await api.saveNotificationTarget(
        {
          outletCode: String(v['outletCode']),
          channel: String(v['channel']),
          address: String(v['address']),
          label: v['label'] === '' ? null : String(v['label'] ?? ''),
          isActive: v['isActive'] !== false,
          kinds: (v['kinds'] ?? []) as string[],
        },
        sunting === 'baru' || sunting === undefined ? undefined : sunting.id,
      );
      setSunting(undefined);
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  async function hapus(id: string) {
    try {
      await api.deleteNotificationTarget(id);
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  async function uji(id: string) {
    try {
      const r = await api.testNotificationTarget(id);
      void (r.sent > 0
        ? message.success(t('notify.testOk'))
        : message.warning(t('notify.testNone')));
      await muat();
    } catch (e) {
      void message.error((e as Error).message);
    }
  }

  const pemilihOutlet = (
    <Select
      allowClear
      style={{ minWidth: 220 }}
      placeholder={t('notify.allOutlets')}
      value={outletCode}
      onChange={setOutletCode}
      data-testid="notify-outlet"
      options={outlets.map((o) => ({ value: o.code, label: `${o.code} — ${o.name}` }))}
    />
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {channels.length === 0 && (
        /*
         * ⛔⛔ Dinyatakan TERANG-TERANGAN. Tanpa ini layarnya terlihat berfungsi penuh: tujuan bisa
         * dibuat (tidak — server menolaknya), peristiwa terus terbit, dan tak ada satu pun tanda
         * kenapa ponsel penerimanya sunyi.
         */
        <Alert
          type="warning"
          showIcon
          data-testid="notify-no-channel"
          message={t('notify.noChannelTitle')}
          description={t('notify.noChannelDesc')}
        />
      )}
      <Tabs
        items={[
          {
            key: 'inbox',
            label: t('notify.tabInbox'),
            children: (
              <Card size="small" title={<Space>{t('notify.tabInbox')}{pemilihOutlet}</Space>}>
                <Table
                  rowKey="id"
                  size="small"
                  loading={sibuk}
                  columns={kolomInbox}
                  dataSource={[...rows]}
                  pagination={{ pageSize: 20 }}
                  data-testid="notify-inbox"
                />
              </Card>
            ),
          },
          {
            key: 'targets',
            label: t('notify.tabTargets'),
            children: (
              <Card
                size="small"
                title={<Space>{t('notify.tabTargets')}{pemilihOutlet}</Space>}
                extra={
                  <Button
                    type="primary"
                    data-testid="notify-add-target"
                    onClick={() => { bukaSunting('baru'); }}
                  >
                    {t('notify.addTarget')}
                  </Button>
                }
              >
                <Table
                  rowKey="id"
                  size="small"
                  loading={sibuk}
                  columns={kolomTarget}
                  dataSource={[...targets]}
                  pagination={false}
                  data-testid="notify-targets"
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        open={sunting !== undefined}
        title={t('notify.targetTitle')}
        onCancel={() => { setSunting(undefined); }}
        onOk={() => void simpan()}
        okButtonProps={{ 'data-testid': 'notify-save-target' } as never}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="outletCode" label={t('notify.colOutlet')} rules={[{ required: true }]}>
            <Select
              options={outlets.map((o) => ({ value: o.code, label: `${o.code} — ${o.name}` }))}
            />
          </Form.Item>
          <Form.Item name="channel" label={t('notify.colChannel')} rules={[{ required: true }]}>
            <Select options={[{ value: 'telegram', label: 'Telegram' }]} />
          </Form.Item>
          <Form.Item
            name="address"
            label={t('notify.colAddress')}
            extra={t('notify.addressHelp')}
            rules={[{ required: true }]}
          >
            <Input data-testid="notify-address" />
          </Form.Item>
          <Form.Item name="label" label={t('notify.colLabel')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="kinds"
            label={t('notify.colKinds')}
            /* ⛔ Server menolak langganan kosong; layar mengatakannya lebih dulu supaya orang tak
               menekan Simpan untuk mengetahuinya. */
            rules={[{ required: true, message: t('notify.needKind') }]}
          >
            <Checkbox.Group
              options={KINDS.map((k) => ({ value: k, label: t(`notify.kind.${k}`) }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
